import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  FaChalkboardTeacher, FaCheckCircle, FaChevronDown, FaCopy, FaKey, FaSearch, FaSpinner,
  FaSyncAlt, FaTimes, FaUserGraduate,
} from 'react-icons/fa';
import { fetchAdminAccounts, resetAccountPassword } from '../../../services/learnService';
import { formatDate } from '../../../utils/formatDate';
import usePagination from '../../../hooks/usePagination';
import useLockBody from '../../../hooks/useLockBody';
import { AdminBadge, AdminSectionHeader, AdminStat } from './AdminListCard';
import AdminEmptyState from './AdminEmptyState';
import Pagination from '../../../components/common/Pagination';
import ConfirmDialog from '../../../components/common/ConfirmDialog';
import toast from 'react-hot-toast';

const PAGE_SIZE = 6;

const initials = (name = '') =>
  name
    .trim()
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase() || '?';

const signInStatus = (u) => {
  if (u.must_change_password)
    return (
      <AdminBadge variant="warning">
        <span className="w-1.5 h-1.5 rounded-full bg-current" aria-hidden="true" />
        Temporary password
      </AdminBadge>
    );
  if (!u.password_set)
    return (
      <AdminBadge variant="default">
        <span className="w-1.5 h-1.5 rounded-full bg-current" aria-hidden="true" />
        Not activated
      </AdminBadge>
    );
  return (
    <AdminBadge variant="success">
      <span className="w-1.5 h-1.5 rounded-full bg-current" aria-hidden="true" />
      Active
    </AdminBadge>
  );
};

/** Shows a just-issued temporary password once. */
const TempPasswordModal = ({ result, onClose }) => {
  const [copied, setCopied] = useState(false);
  useLockBody(!!result);
  if (!result) return null;
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-[2px] p-4 animate-fade-in" role="dialog" aria-modal="true" aria-labelledby="temp-pw-title">
      <div className="bg-white rounded-2xl shadow-[0_24px_80px_-12px_rgba(0,0,0,0.35)] max-w-md w-full p-6 space-y-4 max-h-[90dvh] overflow-y-auto animate-scaleIn">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-500 flex items-center justify-center shrink-0 border border-amber-100">
              <FaKey />
            </div>
            <h3 id="temp-pw-title" className="text-lg font-bold text-designer-dark leading-snug">Temporary password for {result.name}</h3>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="p-1.5 -m-1 text-gray-400 hover:text-gray-700 rounded-lg hover:bg-gray-100"><FaTimes /></button>
        </div>
        <p className="text-sm text-gray-600 leading-relaxed">
          Give this to <strong>{result.email}</strong> privately. It works once: they sign in at <strong>arvixcreative.com/login</strong> and
          must choose a new password straight away. <strong>It won&apos;t be shown again.</strong>
        </p>
        <div className="flex items-center gap-2">
          <code data-testid="temp-password" className="flex-1 min-w-0 px-4 py-3 rounded-xl bg-gray-900 text-white text-base sm:text-lg tracking-wider font-mono select-all truncate">{result.temporary_password}</code>
          <button
            type="button"
            onClick={() => navigator.clipboard?.writeText(result.temporary_password).then(() => setCopied(true))}
            className="px-3 py-3 rounded-xl border border-gray-200 hover:border-designer-primary hover:text-designer-primary text-sm shrink-0 transition-colors"
          >
            <FaCopy className="inline mr-1.5" />{copied ? 'Copied' : 'Copy'}
          </button>
        </div>
        <button type="button" onClick={onClose} className="w-full py-2.5 rounded-xl bg-designer-primary hover:bg-red-600 text-white font-semibold transition-colors">Done</button>
      </div>
    </div>,
    document.body
  );
};

const ResetButton = ({ u, busyId, onReset }) => (
  <button
    type="button"
    onClick={(e) => { e.stopPropagation(); onReset(u); }}
    disabled={busyId === u.id}
    data-testid={`reset-${u.id}`}
    className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 text-xs font-semibold text-gray-700 hover:border-designer-primary hover:text-designer-primary disabled:opacity-50 transition-colors max-sm:w-full"
  >
    {busyId === u.id ? <FaSpinner className="animate-spin" /> : <FaKey />} Reset password
  </button>
);

const AccountHeader = ({ u, avatarClass }) => (
  <div className="flex items-start gap-3">
    <div className={`w-11 h-11 rounded-xl flex items-center justify-center text-white font-bold text-sm shrink-0 shadow-sm ${avatarClass}`} aria-hidden="true">
      {initials(u.name)}
    </div>
    <div className="min-w-0 flex-1">
      <p className="font-semibold text-designer-dark leading-snug break-words">{u.name}</p>
      <a href={`mailto:${u.email}`} onClick={(e) => e.stopPropagation()} className="block text-xs text-gray-500 hover:text-designer-primary break-all mt-0.5 transition-colors" title={`Email ${u.email}`}>
        {u.email}
      </a>
      <div className="flex flex-wrap items-center gap-1.5 mt-2">
        {signInStatus(u)}
        {u.is_test && <AdminBadge variant="info">Test</AdminBadge>}
      </div>
    </div>
  </div>
);

const CardShell = ({ label, expanded, onToggle, children }) => (
  <article
    onClick={onToggle}
    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
    role="button"
    tabIndex={0}
    aria-expanded={expanded}
    aria-label={label}
    className="bg-white rounded-xl border border-gray-200/80 shadow-sm hover:shadow-md hover:border-designer-primary/30 transition-all p-5 flex flex-col gap-4 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-designer-primary"
  >
    {children}
  </article>
);

const ExpandHint = ({ expanded }) => (
  <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-400">
    {expanded ? 'Hide details' : 'View details'}
    <FaChevronDown className={`w-3 h-3 transition-transform duration-300 ${expanded ? 'rotate-180' : ''}`} aria-hidden="true" />
  </span>
);

const TeacherCard = ({ u, busyId, onReset, expanded, onToggle }) => (
  <CardShell label={`${expanded ? 'Hide' : 'View'} details for ${u.name}`} expanded={expanded} onToggle={onToggle}>
    <AccountHeader u={u} avatarClass="bg-gradient-to-br from-designer-primary to-red-700" />
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-2">
        Teaches{u.teaches.length ? ` (${u.teaches.length})` : ''}
      </p>
      {u.teaches.length ? (
        <div className="flex flex-wrap gap-1.5">
          {u.teaches.map((c) => (
            <span key={c.id || c.title} className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-red-50 text-designer-primary">
              {c.title}
            </span>
          ))}
        </div>
      ) : (
        <p className="text-xs text-gray-400">No courses yet.</p>
      )}
    </div>
    {expanded && u.teaches.length > 0 && (
      <div className="space-y-2 rounded-xl bg-gray-50/70 border border-gray-100 p-3">
        {u.teaches.map((c) => (
          <div key={c.id || c.title} className="flex items-center justify-between gap-3">
            <span className="text-sm font-medium text-designer-dark break-words min-w-0">{c.title}</span>
            {c.slug && (
              <a
                href={`/learn/${c.slug}`}
                onClick={(e) => e.stopPropagation()}
                className="text-xs font-semibold text-designer-primary hover:underline shrink-0"
              >
                Preview →
              </a>
            )}
          </div>
        ))}
      </div>
    )}
    <div className="flex items-center justify-between gap-3 pt-3 border-t border-gray-100 mt-auto">
      <ExpandHint expanded={expanded} />
      <ResetButton u={u} busyId={busyId} onReset={onReset} />
    </div>
  </CardShell>
);

const EnrollmentProgress = ({ e }) => {
  const total = Number(e.available_lessons) || 0;
  const done = Number(e.completed_lessons) || 0;
  const pct = total ? Math.round((done / total) * 100) : 0;
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3 text-xs text-gray-600">
        <span className="font-medium text-designer-dark break-words min-w-0 flex-1">{e.title}</span>
        <span className="tabular-nums shrink-0">
          {done}/{total} classes · {Math.round(Number(e.watched_seconds) / 60)} min watched
          {e.last_accessed_at ? ` · last seen ${formatDate(e.last_accessed_at)}` : ''}
        </span>
      </div>
      <div className="mt-1.5 h-1.5 rounded-full bg-gray-100 overflow-hidden" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label={`${e.title} progress`}>
        <div className="h-full bg-gradient-to-r from-designer-primary to-red-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

const StudentCard = ({ u, busyId, onReset, expanded, onToggle }) => {
  const enrollments = u.enrollments || [];
  const totalDone = enrollments.reduce((n, e) => n + (Number(e.completed_lessons) || 0), 0);
  const totalClasses = enrollments.reduce((n, e) => n + (Number(e.available_lessons) || 0), 0);
  const totalMins = enrollments.reduce((n, e) => n + Math.round(Number(e.watched_seconds) / 60), 0);
  return (
    <CardShell label={`${expanded ? 'Hide' : 'View'} details for ${u.name}`} expanded={expanded} onToggle={onToggle}>
      <AccountHeader u={u} avatarClass="bg-gradient-to-br from-designer-secondary to-blue-700" />
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-2">
          Enrollments{enrollments.length ? ` (${enrollments.length})` : ''}
        </p>
        {enrollments.length ? (
          <div className="space-y-3 rounded-xl bg-gray-50/70 border border-gray-100 p-3">
            {enrollments.map((e) => (
              <EnrollmentProgress key={e.id} e={e} />
            ))}
          </div>
        ) : (
          <p className="text-xs text-gray-400">Not enrolled in any course.</p>
        )}
      </div>
      {expanded && enrollments.length > 0 && (
        <div className="grid grid-cols-3 gap-2 text-center">
          {[
            { v: `${totalDone}/${totalClasses}`, l: 'Classes done' },
            { v: `${totalMins} min`, l: 'Watched' },
            { v: enrollments.length, l: 'Courses' },
          ].map((s) => (
            <div key={s.l} className="rounded-xl bg-gray-50/70 border border-gray-100 px-2 py-2.5">
              <p className="font-bold text-designer-dark tabular-nums text-sm">{s.v}</p>
              <p className="text-[11px] text-gray-400 mt-0.5">{s.l}</p>
            </div>
          ))}
        </div>
      )}
      <div className="flex items-center justify-between gap-3 pt-3 border-t border-gray-100 mt-auto">
        <ExpandHint expanded={expanded} />
        <ResetButton u={u} busyId={busyId} onReset={onReset} />
      </div>
    </CardShell>
  );
};

const AdminAccounts = ({ getHeaders }) => {
  const [accounts, setAccounts] = useState(null);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [result, setResult] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(() => {
    setError('');
    return fetchAdminAccounts(getHeaders())
      .then(setAccounts)
      .catch((err) => setError(err.message));
  }, [getHeaders]);

  useEffect(() => { load(); }, [load]);

  const refresh = () => {
    setRefreshing(true);
    load().finally(() => setRefreshing(false));
  };

  const q = query.trim().toLowerCase();
  const filtered = useMemo(() => (
    (accounts || []).filter((u) => !q || `${u.name} ${u.email}`.toLowerCase().includes(q))
  ), [accounts, q]);
  const teachers = filtered.filter((u) => u.role === 'teacher');
  const students = filtered.filter((u) => u.role === 'student');
  const activeCount = (accounts || []).filter((u) => u.password_set && !u.must_change_password).length;

  const teachersPager = usePagination(teachers, PAGE_SIZE, { resetKey: q });
  const studentsPager = usePagination(students, PAGE_SIZE, { resetKey: q });

  const [confirmReset, setConfirmReset] = useState(null);

  const reset = async (u) => {
    setBusyId(u.id);
    try {
      setResult(await resetAccountPassword(u.id, getHeaders()));
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusyId(null);
    }
  };

  if (error) return <p className="text-sm text-red-600">{error}</p>;
  if (!accounts) return <p className="text-sm text-gray-500"><FaSpinner className="inline animate-spin mr-2" />Loading accounts…</p>;

  const toggle = (id) => setExpandedId((prev) => (prev === id ? null : id));

  return (
    <div className="space-y-8 animate-fadeIn">
      <div className="grid grid-cols-2 xl:grid-cols-3 gap-3">
        <AdminStat icon={FaChalkboardTeacher} label="Teachers" value={accounts.filter((u) => u.role === 'teacher').length} tile="bg-red-50 text-designer-primary" />
        <AdminStat icon={FaUserGraduate} label="Students" value={accounts.filter((u) => u.role === 'student').length} tile="bg-blue-50 text-blue-600" />
        <AdminStat icon={FaCheckCircle} label="Active accounts" value={activeCount} sub={`of ${accounts.length} total`} tile="bg-green-50 text-green-600" />
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 w-3.5 h-3.5 pointer-events-none" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name or email…"
            aria-label="Search accounts"
            className="w-full pl-10 pr-9 py-2.5 rounded-xl border border-gray-200 bg-white focus:border-designer-primary outline-none text-sm shadow-sm"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              aria-label="Clear search"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
            >
              <FaTimes className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={refresh}
          disabled={refreshing}
          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm font-semibold text-gray-600 hover:text-designer-primary hover:border-designer-primary/40 transition-colors disabled:opacity-50 shadow-sm shrink-0"
        >
          <FaSyncAlt className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>
      {q && (
        <p className="-mt-5 text-xs text-gray-400" aria-live="polite">
          {teachers.length} teacher{teachers.length === 1 ? '' : 's'} · {students.length} student{students.length === 1 ? '' : 's'} match &ldquo;{query.trim()}&rdquo;
        </p>
      )}

      <section aria-labelledby="teachers-h">
        <AdminSectionHeader id="teachers-h" icon={FaChalkboardTeacher} title={`Teachers (${teachers.length})`} />
        {teachers.length ? (
          <>
            <div className="grid gap-4 md:grid-cols-2" ref={teachersPager.listRef}>
              {teachersPager.items.map((u) => (
                <TeacherCard key={u.id} u={u} busyId={busyId} onReset={setConfirmReset} expanded={expandedId === u.id} onToggle={() => toggle(u.id)} />
              ))}
            </div>
            <Pagination currentPage={teachersPager.page} totalPages={teachersPager.totalPages} onPageChange={teachersPager.changePage} />
          </>
        ) : (
          <AdminEmptyState
            icon={FaChalkboardTeacher}
            title={q ? 'No matching teachers' : 'No teachers yet'}
            description={q ? 'Try a different search.' : 'Teacher accounts will appear here once registered.'}
          />
        )}
      </section>

      <section aria-labelledby="students-h">
        <AdminSectionHeader id="students-h" icon={FaUserGraduate} title={`Students (${students.length})`} />
        {students.length ? (
          <>
            <div className="grid gap-4 md:grid-cols-2" ref={studentsPager.listRef}>
              {studentsPager.items.map((u) => (
                <StudentCard key={u.id} u={u} busyId={busyId} onReset={setConfirmReset} expanded={expandedId === u.id} onToggle={() => toggle(u.id)} />
              ))}
            </div>
            <Pagination currentPage={studentsPager.page} totalPages={studentsPager.totalPages} onPageChange={studentsPager.changePage} />
          </>
        ) : (
          <AdminEmptyState
            icon={FaUserGraduate}
            title={q ? 'No matching students' : 'No students yet'}
            description={q ? 'Try a different search.' : 'Student accounts will appear here once registered.'}
          />
        )}
      </section>

      <TempPasswordModal result={result} onClose={() => setResult(null)} />

      <ConfirmDialog
        isOpen={!!confirmReset}
        title="Reset password?"
        message={confirmReset ? `Reset the password for ${confirmReset.name} (${confirmReset.email})? Their current password stops working and a one-time temporary password is issued.` : ''}
        confirmText="Reset password"
        type="warning"
        onClose={() => setConfirmReset(null)}
        onConfirm={() => { const u = confirmReset; setConfirmReset(null); reset(u); }}
      />
    </div>
  );
};

export default AdminAccounts;
